import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  CreditCard, QrCode, ExternalLink, Copy, CheckCircle2,
  Calendar, Shield, Gem, Loader2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANOS = [
  { key: "mensal_mensal",   label: "Mensal",          price: "R$ 14,90/mês",  icon: <Calendar className="w-5 h-5 text-blue-400" />,    hotmart: process.env.REACT_APP_HOTMART_MENSAL          || "https://pay.hotmart.com/U106046685M" },
  { key: "essencial_anual", label: "Anual Essencial", price: "R$ 99,90/ano",  icon: <Shield   className="w-5 h-5 text-emerald-400" />, hotmart: process.env.REACT_APP_HOTMART_ANUAL_ESSENCIAL || "https://pay.hotmart.com/C106046581R?off=x84kn24y" },
  { key: "premium_anual",   label: "Anual Premium",   price: "R$ 139,90/ano", icon: <Gem      className="w-5 h-5 text-amber-400" />,   hotmart: process.env.REACT_APP_HOTMART_ANUAL_PREMIUM  || "https://pay.hotmart.com/K106046660U?off=47s9loc0" },
];

const TABS = ["Mercado Pago", "Hotmart", "PIX Avulso"];

export default function PaymentModal({ open, onOpenChange, userEmail = "", userName = "" }) {
  const [tab, setTab] = useState(0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden border border-slate-800 bg-slate-900">
        <div className="bg-gradient-to-r from-emerald-600/20 via-blue-700/20 to-slate-900 px-6 pt-6 pb-4 border-b border-slate-800">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">Escolha seu Plano</DialogTitle>
          </DialogHeader>
          <div className="flex gap-1 mt-4">
            {TABS.map((t, i) => (
              <button
                key={t}
                onClick={() => setTab(i)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  tab === i
                    ? "bg-emerald-500 text-slate-950"
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {tab === 0 && <MercadoPagoTab userEmail={userEmail} />}
          {tab === 1 && <HotmartTab />}
          {tab === 2 && <PixAvulsoTab userEmail={userEmail} userName={userName} onSuccess={() => onOpenChange(false)} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Tab 1: Mercado Pago ──────────────────────────────────────────────────────

function MercadoPagoTab({ userEmail }) {
  const [plano, setPlano]     = useState("essencial_anual");
  const [metodo, setMetodo]   = useState("pix");
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [copied, setCopied]   = useState(false);

  const gerar = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/pagamento/mercadopago/preferencia`, {
        plano, email: userEmail, metodo,
      });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar pagamento.");
    } finally {
      setLoading(false);
    }
  };

  const copyQr = () => {
    navigator.clipboard.writeText(result.qr_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-5">
      {!result ? (
        <>
          <div>
            <p className="text-xs text-slate-400 mb-2 font-medium uppercase tracking-wide">Plano</p>
            <div className="space-y-2">
              {PLANOS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPlano(p.key)}
                  className={`w-full flex items-center justify-between rounded-xl px-4 py-3 border transition-colors text-left ${
                    plano === p.key
                      ? "border-emerald-500 bg-emerald-500/10"
                      : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {p.icon}
                    <span className="font-medium text-white text-sm">{p.label}</span>
                  </div>
                  <span className="text-emerald-400 font-bold text-sm">{p.price}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs text-slate-400 mb-2 font-medium uppercase tracking-wide">Forma de Pagamento</p>
            <div className="flex gap-2">
              {[
                { key: "pix",    label: "PIX",    icon: <QrCode className="w-4 h-4" /> },
                { key: "cartao", label: "Cartão", icon: <CreditCard className="w-4 h-4" /> },
                { key: "boleto", label: "Boleto", icon: <Shield className="w-4 h-4" /> },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() => setMetodo(m.key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-medium border transition-colors ${
                    metodo === m.key
                      ? "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                      : "border-slate-700 text-slate-400 hover:border-slate-600 hover:text-white"
                  }`}
                >
                  {m.icon} {m.label}
                </button>
              ))}
            </div>
          </div>

          <Button onClick={gerar} disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Gerar Pagamento
          </Button>
        </>
      ) : (
        <div className="space-y-4">
          {result.qr_code && (
            <>
              <p className="text-center text-sm text-slate-300">Escaneie o QR Code ou copie o código PIX:</p>
              {result.qr_base64 && (
                <div className="flex justify-center">
                  <img
                    src={`data:image/png;base64,${result.qr_base64}`}
                    alt="QR Code PIX"
                    className="w-48 h-48 rounded-xl border border-slate-700 bg-white p-2"
                  />
                </div>
              )}
              <button
                onClick={copyQr}
                className="w-full flex items-center justify-between rounded-xl bg-slate-800 border border-slate-700 px-4 py-3 text-xs text-slate-300 hover:border-emerald-500/50 transition-colors"
              >
                <span className="truncate mr-2 font-mono">{result.qr_code.slice(0, 48)}…</span>
                {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> : <Copy className="w-4 h-4 text-slate-400 shrink-0" />}
              </button>
              <p className="text-xs text-slate-500 text-center">
                Após o pagamento, seu acesso é liberado automaticamente em até 1 minuto.
              </p>
            </>
          )}
          {result.init_point && !result.qr_code && (
            <>
              <p className="text-center text-sm text-slate-300">Clique para finalizar o pagamento no Mercado Pago:</p>
              <a
                href={result.init_point}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl transition-colors"
              >
                Pagar agora <ExternalLink className="w-4 h-4" />
              </a>
            </>
          )}
          <Button variant="ghost" onClick={() => setResult(null)} className="w-full text-slate-500 hover:text-white text-sm">
            ← Voltar
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Tab 2: Hotmart ───────────────────────────────────────────────────────────

function HotmartTab() {
  const plans = [
    { label: "Mensal",          price: "R$ 14,90/mês",  url: PLANOS[0].hotmart, icon: <Calendar className="w-5 h-5 text-blue-400" />,   desc: "Acesso mensal ao app" },
    { label: "Anual Essencial", price: "R$ 99,90/ano",  url: PLANOS[1].hotmart, icon: <Shield   className="w-5 h-5 text-emerald-400" />, desc: "Acesso anual + módulos essenciais" },
    { label: "Anual Premium",   price: "R$ 139,90/ano", url: PLANOS[2].hotmart, icon: <Gem      className="w-5 h-5 text-amber-400" />,   desc: "Acesso anual + todos os módulos" },
  ];

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-400 mb-1">Pagamento seguro via Hotmart (cartão ou PIX). O acesso é liberado automaticamente após confirmação.</p>
      {plans.map((p) => (
        <a
          key={p.label}
          href={p.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between rounded-xl p-4 bg-slate-800/50 border border-slate-700 hover:border-emerald-500/40 transition-colors group"
        >
          <div className="flex items-center gap-3">
            {p.icon}
            <div>
              <div className="font-semibold text-white text-sm">{p.label}</div>
              <div className="text-xs text-slate-400 mt-0.5">{p.desc}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            <span className="text-emerald-400 font-bold text-sm">{p.price}</span>
            <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 transition-colors" />
          </div>
        </a>
      ))}
      <p className="text-xs text-slate-500 text-center pt-2">
        Use o mesmo e-mail do cadastro no Monyxx para o plano ser vinculado automaticamente.
      </p>
    </div>
  );
}

// ── Tab 3: PIX Avulso ────────────────────────────────────────────────────────

function PixAvulsoTab({ userEmail, userName, onSuccess }) {
  const [form, setForm] = useState({
    email:        userEmail || "",
    nome:         userName  || "",
    plano:        "essencial_anual",
    comprovante:  "",
  });
  const [loading, setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const PIX_CHAVE = process.env.REACT_APP_PIX_CHAVE || "suporte@monyxx.com.br";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.nome || !form.comprovante) {
      toast.error("Preencha todos os campos.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await axios.post(`${API}/pagamento/pix/comprovante`, fd);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar comprovante.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center text-center py-4 gap-4">
        <CheckCircle2 className="w-14 h-14 text-emerald-400" />
        <h3 className="font-display text-lg font-bold text-white">Comprovante recebido!</h3>
        <p className="text-slate-400 text-sm">Seu acesso será liberado manualmente em até 24h após confirmação do PIX.</p>
        <Button onClick={onSuccess} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold w-full">
          OK, entendido
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="rounded-xl bg-slate-800/60 border border-slate-700 p-4 text-sm text-slate-300 space-y-1">
        <p className="font-semibold text-white">Chave PIX:</p>
        <p className="font-mono text-emerald-400 break-all">{PIX_CHAVE}</p>
        <p className="text-xs text-slate-500 mt-1">Faça o PIX, depois preencha os campos abaixo com o comprovante.</p>
      </div>

      <div>
        <p className="text-xs text-slate-400 mb-2 font-medium uppercase tracking-wide">Plano desejado</p>
        <div className="space-y-2">
          {PLANOS.map((p) => (
            <button
              type="button"
              key={p.key}
              onClick={() => set("plano", p.key)}
              className={`w-full flex items-center justify-between rounded-xl px-4 py-3 border transition-colors text-left ${
                form.plano === p.key
                  ? "border-emerald-500 bg-emerald-500/10"
                  : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
              }`}
            >
              <div className="flex items-center gap-3">
                {p.icon}
                <span className="text-sm font-medium text-white">{p.label}</span>
              </div>
              <span className="text-emerald-400 font-bold text-sm">{p.price}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-400 font-medium uppercase tracking-wide block mb-1">Nome</label>
          <Input
            value={form.nome}
            onChange={(e) => set("nome", e.target.value)}
            placeholder="Seu nome"
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 font-medium uppercase tracking-wide block mb-1">E-mail</label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="email@exemplo.com"
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-400 font-medium uppercase tracking-wide block mb-1">Comprovante / Código de transação</label>
        <textarea
          value={form.comprovante}
          onChange={(e) => set("comprovante", e.target.value)}
          placeholder="Cole aqui o ID da transação ou descreva o comprovante..."
          rows={3}
          className="w-full rounded-xl bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
        />
      </div>

      <Button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        Enviar Comprovante
      </Button>
    </form>
  );
}
